import React from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { InputSwitch } from "primereact/inputswitch";
import { Calendar } from "primereact/calendar";
import { Message } from "primereact/message";
import { MILE_TYPES } from "./mileTypes";

/**
 * Создание и правка тарифной карты.
 *
 * Ни услуги, ни перевозчика у карты нет: это цена партнёра на направление, а кто
 * повезёт — решает маршрутизация.
 *
 * Плечо задаётся только при создании: содержимое карты — это маршруты именно
 * этого плеча, и смена плеча оставила бы карту первой мили набитой магистралью.
 */
const PlanDialog = ({ visible, onHide, onSubmit, loading, plan }) => {
  const [name, setName] = React.useState("");
  const [mileType, setMileType] = React.useState(null);
  const [isActive, setIsActive] = React.useState(false);
  const [validFrom, setValidFrom] = React.useState(null);
  const [validTo, setValidTo] = React.useState(null);

  const isEdit = Boolean(plan);

  React.useEffect(() => {
    if (!visible) return;
    setName(plan?.name ?? "");
    setMileType(plan?.mileType ?? null);
    setIsActive(plan?.isActive ?? false);
    setValidFrom(plan?.validFrom ? new Date(plan.validFrom) : null);
    setValidTo(plan?.validTo ? new Date(plan.validTo) : null);
  }, [visible, plan]);

  const periodBroken = validFrom && validTo && validFrom > validTo;
  const canSave = Boolean(name?.trim()) && Boolean(mileType) && !periodBroken;

  const handleSave = () => {
    if (!canSave) return;
    onSubmit({
      name: name.trim(),
      mileType,
      isActive,
      validFrom: asDate(validFrom),
      validTo: asDate(validTo),
    });
  };

  const footer = (
    <div>
      <Button label="Отмена" icon="pi pi-times" className="p-button-text" onClick={onHide} />
      <Button label="Сохранить" icon="pi pi-check" loading={loading} disabled={!canSave} onClick={handleSave} />
    </div>
  );

  return (
    <Dialog header={isEdit ? `Карта: ${plan.name}` : "Новая тарифная карта"}
            visible={visible} style={{ width: "620px" }} modal onHide={onHide} footer={footer}>
      <div className="p-fluid p-formgrid p-grid">
        <div className="p-field p-col-12">
          <label>Название</label>
          <InputText value={name} onChange={(e) => setName(e.target.value)}
                     placeholder="например, тариф Весна" />
          <small className="p-d-block">По нему карту выбирают при назначении партнёру.</small>
        </div>

        <div className="p-field p-col-12 p-md-6">
          <label>Плечо доставки</label>
          <Dropdown value={mileType} options={MILE_TYPES} disabled={isEdit}
                    placeholder="Выберите плечо" onChange={(e) => setMileType(e.value)} />
          {isEdit && (
            <small className="p-d-block">
              Плечо не меняется: в карте уже лежат маршруты именно этого плеча.
            </small>
          )}
        </div>

        <div className="p-field p-col-12 p-md-6">
          <label>Действует с</label>
          <Calendar value={validFrom} dateFormat="dd.mm.yy" showIcon showButtonBar
                    onChange={(e) => setValidFrom(e.value ?? null)} />
        </div>

        <div className="p-field p-col-12 p-md-6">
          <label>Действует по</label>
          <Calendar value={validTo} dateFormat="dd.mm.yy" showIcon showButtonBar
                    onChange={(e) => setValidTo(e.value ?? null)} />
        </div>

        <div className="p-field p-col-12 p-md-6">
          <label>Карта включена</label>
          <div>
            <InputSwitch checked={isActive} onChange={(e) => setIsActive(e.value)} />
          </div>
          <small className="p-d-block">Выключенная карта в расчёт не попадает.</small>
        </div>

        {periodBroken && (
          <div className="p-col-12">
            <Message severity="error" text="Начало срока действия позже окончания." />
          </div>
        )}
      </div>
    </Dialog>
  );
};

/** Календарь отдаёт Date, серверу нужна дата без времени и часового пояса. */
const asDate = (value) => {
  if (!value) return null;
  const pad = (n) => String(n).padStart(2, "0");
  return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
};

export default PlanDialog;
